import {Helmet} from '@dr.pogodin/react-helmet';
import {
  closeCurrentDialog,
  closeDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {isNotNil} from 'ramda';
import {isArray, isString} from 'ramda-adjunct';

import {useDeleteArticleMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  Main,
  catchUnhandledDataGridActions,
  handleApiError,
  queryParams,
  refreshDmsDatagrid,
  usePermissions,
  useWarehouseDataGridTreeFolder,
} from '@dms/shared';
import {ArticleCreate, getSingleOrUndefined} from '@dms/shared-aftersales';
import {WAREHOUSE_PDF_DEFINITIONS, useWarehouseGridPdfExport} from '@dms/warehouse/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate, useQueryState} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {ArticleMoveToFolder} from '../../components/ArticleMoveToFolder';
import {ArticleListExternalFilters} from './components/ArticleListExternalFilters';
import {WorkItemGroupDialog} from './components/WorkItemGroupDialog';

function isFilterRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const ARTICLES_EXTERNAL_FILTER_ID = 'articles-external-filter';
const PREDEFINED_COLUMNS = ['warehouseId'] as const;
const WAREHOUSE_ARTICLES_GRID_CODE = 'warehouse-articles';

export function ArticleList() {
  const onPdfExportCallback = useWarehouseGridPdfExport(
    i18n.t('entity.warehouse.labels.articles'),
    WAREHOUSE_PDF_DEFINITIONS.articles
  );
  const navigate = useNavigate();

  const dataGridFilters = useRef<Record<string, unknown>>({});

  const [folderTreeRef, dataGridModifier, activeFolderId] = useWarehouseDataGridTreeFolder();
  const [deleteArticle] = useDeleteArticleMutation();

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const [catalogNumber] = useQueryState(queryParams.CATALOG_NUMBER);

  const handleNewArticle = () => {
    openDialog(
      <ArticleCreate
        defaultValues={{
          catalogNumber:
            'catalogNumber' in dataGridFilters.current
              ? (dataGridFilters.current.catalogNumber as string)
              : undefined,
          warehouseId:
            'warehouseId' in dataGridFilters.current
              ? getSingleOrUndefined(dataGridFilters.current.warehouseId)
              : undefined,
          manufacturerId:
            'manufacturerId' in dataGridFilters.current
              ? getSingleOrUndefined(dataGridFilters.current.manufacturerId)
              : undefined,
          name:
            'name' in dataGridFilters.current
              ? (dataGridFilters.current.name as string)
              : undefined,
          activeFolderId,
        }}
        onArticleCreate={(createdArticleData) => {
          if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
            return navigate(
              composePath(warehouseRoutes.articleDetailOverview, {
                params: {
                  warehouseId: createdArticleData.formValues.warehouseId,
                  id: createdArticleData.articleId,
                },
              })
            );
          }

          closeCurrentDialog();
          refreshDmsDatagrid(WAREHOUSE_ARTICLES_GRID_CODE);
        }}
        onClose={() => closeDialog('createNewArticleDialog')}
        hasCreateAndEditButton
        data-testid={testIds.warehouse.articleList('createNewArticle')}
      />,
      {
        id: 'createNewArticleDialog',
        title: i18n.t('general.labels.createNew'),
        scrollBehavior: 'outside',
        withAdditionalFooter: true,
      }
    );
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    sourceSearchParams,
    deselectAll,
  }) => {
    const itemsIds = isArray(rowId) ? rowId : [rowId];

    match(actionKey)
      .with('redirectDetail', () => {
        const warehouseId = getColumnData<{key: string}>('warehouseId')?.value.key;
        if (isString(rowId) && isNotNil(warehouseId)) {
          navigate(
            composePath(warehouseRoutes.articleDetailOverview, {
              queryParams: sourceSearchParams,
              params: {
                id: rowId,
                warehouseId,
              },
              isSearchParamsPreserved: false,
            })
          );
        }
      })
      .with('moveToFolder', () =>
        openDialog(
          <ArticleMoveToFolder
            itemIds={itemsIds}
            onClose={closeCurrentDialog}
            data-testid={testIds.warehouse.articleList('articleMoveDialog')}
          />,
          {
            size: 'small',
            title: i18n.t('entity.addWork.labels.moveToFolder'),
          }
        )
      )
      .with('workItemGroup', () =>
        openDialog(
          <WorkItemGroupDialog
            articleIds={itemsIds}
            onClose={closeCurrentDialog}
            onSuccess={() => refreshDmsDatagrid(WAREHOUSE_ARTICLES_GRID_CODE, {deselectAll: true})}
          />,
          {
            size: 'small',
            title: i18n.t('entity.addWork.labels.workItemGroup'),
          }
        )
      )
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteArticle({articleId: itemsIds})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshDmsDatagrid(WAREHOUSE_ARTICLES_GRID_CODE, {deselectAll: true}))
              .then(deselectAll)
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('module.warehouse.title')} />
      <Main
        isFullHeight
        heading={i18n.t('entity.warehouse.labels.articles')}
        actions={[
          {
            type: 'button',
            title: i18n.t('page.warehouse.labels.newArticle'),
            onClick: handleNewArticle,
            isDisabled: !articleCreate,
            tooltip: !articleCreate
              ? i18n.t('module.warehouse.label.missingArticleCreatePermission')
              : undefined,
            'data-testid': testIds.warehouse.articleList('newArticle'),
          },
        ]}
      >
        <FullscreenDataGridWrapper
          externalFilterId={ARTICLES_EXTERNAL_FILTER_ID}
          folderTreeRef={folderTreeRef}
          data-testid={testIds.warehouse.articleList('externalFilterWithFolderTree')}
        >
          <DmsDataGrid
            // DG must be re-rendered on folder change to update the query modifier
            // eslint-disable-next-line eslint-js/no-restricted-syntax
            key={dataGridModifier?.key}
            gridCode={WAREHOUSE_ARTICLES_GRID_CODE}
            pdfColumns={WAREHOUSE_PDF_DEFINITIONS.articles.columns}
            onPdfExportCallback={onPdfExportCallback}
            actionCallback={actionCallback}
            predefinedColumns={PREDEFINED_COLUMNS}
            onFilterChanged={(filters) => {
              if (isFilterRecord(filters)) {
                dataGridFilters.current = filters;
              }
            }}
            emptyState={{
              headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
            }}
            queryModifier={dataGridModifier?.queryModifier}
            _useAsLastResort_definitionBehaviorOverrides={{
              filterMode: 'CUSTOM',
              wrapperStyle: 'CARD',
            }}
            initialSmartSearch={catalogNumber ?? ''}
            externalFilterId={ARTICLES_EXTERNAL_FILTER_ID}
            filterComponent={ArticleListExternalFilters}
            data-testid={WAREHOUSE_ARTICLES_GRID_CODE}
          />
        </FullscreenDataGridWrapper>
      </Main>
    </>
  );
}
